import { HeaderBreadcrumbs } from '@/components/layout/HeaderBreadcrumbs';
import { usePageChrome } from '@/contexts/PageChromeContext';
import { useHeaderScrollToTop } from '@/hooks/useHeaderScrollToTop';
import { useLanguage } from '@/i18n/language-hooks';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { AppWordmark } from './AppWordmark';
import { PageEditMenu } from './PageEditMenu';
import { UserMenu } from './UserMenu';

interface AppHeaderProps {
  isAuthenticated: boolean;
  onSearchOpen: () => void;
}

export const AppHeader = ({ isAuthenticated, onSearchOpen }: AppHeaderProps) => {
  const { t } = useLanguage();
  const {
    breadcrumbEntries,
    extraSegments,
    headerActions,
    editMenuExtensions,
    scrollRef,
    pageLabelHref,
  } = usePageChrome();

  const breadcrumb = breadcrumbEntries.at(-1)?.value;
  const pageSegmentIsLink = extraSegments.length > 0;
  const scrollToTop = useHeaderScrollToTop(scrollRef);

  const isMac = useMemo(
    () => typeof navigator !== 'undefined' && /Mac|iPhone|iPod|iPad/i.test(navigator.platform),
    []
  );

  const renderHeaderActions = () =>
    headerActions.length > 0 ? (
      <div className='flex shrink-0 items-center gap-2'>
        {headerActions.map(({ key, node }) => (
          <Fragment key={key}>{node}</Fragment>
        ))}
      </div>
    ) : null;

  const renderBreadcrumbTrail = (className: string) =>
    breadcrumb ? (
      <HeaderBreadcrumbs
        items={breadcrumb.items}
        extraSegments={extraSegments}
        pageSegmentIsLink={pageSegmentIsLink}
        pageLabelHref={pageLabelHref}
        scrollToTop={scrollToTop}
        className={className}
      />
    ) : null;

  // Full-width on mobile (flex-1), fixed-size on desktop (w-72).
  const search = (
    <button
      type='button'
      onClick={onSearchOpen}
      className={cn(
        'text-muted-foreground hover:bg-accent hover:text-accent-foreground bg-background',
        'flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border px-3 text-sm transition-colors',
        'md:w-72 md:flex-none'
      )}
      aria-label={t('search.placeholder')}
    >
      <Search className='size-4 shrink-0' aria-hidden />
      <span className='truncate'>{t('search.placeholder')}</span>
      <kbd
        aria-hidden
        className='bg-muted text-muted-foreground pointer-events-none ml-auto hidden items-center gap-0.5 rounded border px-1 font-mono text-[10px] select-none sm:inline-flex'
      >
        <span className='text-xs'>{isMac ? '⌘' : 'Ctrl+'}</span>K
      </kbd>
    </button>
  );

  // The mobile context row only earns its space when it has breadcrumbs, edit menu, or actions.
  const showMobileContextRow =
    Boolean(breadcrumb) || headerActions.length > 0 || editMenuExtensions.length > 0;

  return (
    <header className='bg-fin-sidebar sticky top-0 z-30 flex shrink-0 flex-col'>
      <div className='flex items-center gap-2 px-4 py-2.5 md:pr-6 md:pl-0'>
        {/* Mobile-only app logo */}
        <AppWordmark name={t('app.name')} showName={false} className='shrink-0 md:hidden' />

        {/* Desktop-only context group: breadcrumbs → edit → add, then flexible empty space */}
        <div className='hidden min-w-0 flex-1 items-center gap-2 md:flex'>
          {renderBreadcrumbTrail('min-w-0 shrink')}
          <PageEditMenu />
          {renderHeaderActions()}
        </div>

        {/* Search + user menu: full-width search on mobile, fixed on the right on desktop */}
        <div className='flex min-w-0 flex-1 items-center gap-1 md:ml-auto md:flex-none'>
          {search}
          {isAuthenticated && <UserMenu />}
        </div>
      </div>

      {/* Mobile-only second row: breadcrumbs → edit → add */}
      {showMobileContextRow ? (
        <div className='border-fin-gray-800/80 flex items-center gap-2 overflow-x-auto border-t px-4 py-1.5 md:hidden'>
          {renderBreadcrumbTrail('min-w-0 flex-1') ?? <div className='min-w-0 flex-1' />}
          <PageEditMenu />
          {renderHeaderActions()}
        </div>
      ) : null}
    </header>
  );
};

export default AppHeader;
